import { Fragment, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import AsyncSelect from "react-select/async";
import toast from "react-hot-toast";
import { privateAxios } from "../utils/axios";

const SERVICE_TYPE_OPTIONS = [
  "Monitor Service",
  "IT Product Service",
  "Printer Service",
  "Network Service",
  "Other",
];

export default function AddServiceRequest({ onClose, onSuccess }) {
  const [open] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const cancelButtonRef = useRef(null);

  const [selectedBranch, setSelectedBranch] = useState(null);

  const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");

  const [form, setForm] = useState({
    branchName: "",
    receiverName: "",
    preparedBy: loggedInUser?.name || "",
    serviceType: "",
    description: "",
  });

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const loadBranchOptions = async (inputValue, callback) => {
    if (!inputValue || inputValue.trim().length < 1) {
      callback([]);
      return;
    }
    try {
      const res = await privateAxios.get("/branches", {
        params: { q: inputValue, limit: 20 },
      });
      const branches = res.data?.data?.branches || [];
      callback(
        branches.map((b) => ({
          value: b._id,
          label: `${b.name} - ${b.receiverName}-${b.receiverNumber}`,
          branch: b,
        })),
      );
    } catch (err) {
      callback([]);
    }
  };

  const handleBranchSelect = (option) => {
    setSelectedBranch(option);
    if (!option) return;
    const b = option.branch;
    setForm((prev) => ({
      ...prev,
      branchName: b.name || "",
      receiverName: b.receiverName || "",
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.branchName.trim()) newErrors.branchName = "Branch name is required";
    if (!form.receiverName.trim()) newErrors.receiverName = "Receiver name is required";
    if (!form.preparedBy.trim()) newErrors.preparedBy = "Prepared by is required";
    if (!form.serviceType.trim()) newErrors.serviceType = "Service type is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      const res = await privateAxios.post("/service-requests/", form);
      if (res.data.success) {
        toast.success("Service request saved!");
        onSuccess();
        onClose();
      } else {
        toast.error(res.data.message || "Failed to save");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const err = (field) =>
    errors[field] ? (
      <p className="mt-1 text-sm text-red-600">{errors[field]}</p>
    ) : null;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-60 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-xl transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all">
                <button
                  type="button"
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
                  onClick={onClose}
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>

                <Dialog.Title className="text-xl font-semibold text-gray-900 mb-6 text-left">
                  New Service Request
                </Dialog.Title>

                <form onSubmit={handleSubmit} className="space-y-4 text-left">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Search Branch (optional)
                    </label>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions={false}
                      value={selectedBranch}
                      loadOptions={loadBranchOptions}
                      onChange={handleBranchSelect}
                      isClearable
                      placeholder="Search by branch name, number, code or receiver number..."
                      noOptionsMessage={({ inputValue }) =>
                        inputValue ? "No branches found" : "Type to search branches"
                      }
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      Selecting a branch auto-fills Branch Name and Receiver Name below. You can still edit them manually.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Branch Name *</label>
                      <input
                        type="text"
                        value={form.branchName}
                        onChange={(e) => setField("branchName", e.target.value)}
                        className={`mt-1 block w-full rounded-md border ${errors.branchName ? "border-red-300" : "border-gray-300"} px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                      />
                      {err("branchName")}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Receiver Name *</label>
                      <input
                        type="text"
                        value={form.receiverName}
                        onChange={(e) => setField("receiverName", e.target.value)}
                        className={`mt-1 block w-full rounded-md border ${errors.receiverName ? "border-red-300" : "border-gray-300"} px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                      />
                      {err("receiverName")}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Prepared By *</label>
                    <input
                      type="text"
                      value={form.preparedBy}
                      readOnly
                      className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-600 cursor-not-allowed"
                    />
                    {err("preparedBy")}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Service Type *</label>
                    <input
                      type="text"
                      list="service-type-options"
                      value={form.serviceType}
                      onChange={(e) => setField("serviceType", e.target.value)}
                      placeholder="e.g. Monitor Service, IT Product Service..."
                      className={`mt-1 block w-full rounded-md border ${errors.serviceType ? "border-red-300" : "border-gray-300"} px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    />
                    <datalist id="service-type-options">
                      {SERVICE_TYPE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt} />
                      ))}
                    </datalist>
                    {err("serviceType")}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      rows={3}
                      value={form.description}
                      onChange={(e) => setField("description", e.target.value)}
                      placeholder="Describe the issue or service needed (optional)"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      ref={cancelButtonRef}
                      onClick={onClose}
                      className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      {loading ? "Saving..." : "Save Request"}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
